import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { PsfColors } from '../theme';
import {
  formatOnemapLocationLabel,
  parseOnemapCoordinates,
  searchOnemapLocations,
  type OneMapSearchResult,
} from '../../super-picker/utils/onemapLocationSearch';

export interface ReferenceRadiusValue {
  label: string;
  latitude: number;
  longitude: number;
  radiusKm: number;
}

interface ReferenceRadiusFilterProps {
  c: PsfColors;
  onChange: (value: ReferenceRadiusValue | null) => void;
  resetKey?: number;
  emphasis?: boolean;
}

export default function ReferenceRadiusFilter({ c, onChange, resetKey = 0, emphasis = false }: ReferenceRadiusFilterProps) {
  const [locationInput, setLocationInput] = useState('');
  const [locationOptions, setLocationOptions] = useState<OneMapSearchResult[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<OneMapSearchResult | null>(null);
  const [locationSearching, setLocationSearching] = useState(false);
  const [radiusInput, setRadiusInput] = useState('');

  const coordinates = useMemo(
    () => selectedLocation ? parseOnemapCoordinates(selectedLocation) : null,
    [selectedLocation],
  );
  const radiusKm = useMemo(() => {
    const parsed = Number(radiusInput.trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [radiusInput]);
  const radiusMissing = coordinates != null && radiusKm == null;

  useEffect(() => {
    const query = locationInput.trim();
    if (!query) {
      setLocationOptions([]);
      setLocationSearching(false);
      return;
    }
    let cancelled = false;
    setLocationSearching(true);
    const timer = window.setTimeout(() => {
      void searchOnemapLocations(query)
        .then((results) => { if (!cancelled) setLocationOptions(results); })
        .catch(() => { if (!cancelled) setLocationOptions([]); })
        .finally(() => { if (!cancelled) setLocationSearching(false); });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [locationInput]);

  useEffect(() => {
    if (!coordinates || radiusKm == null || !selectedLocation) {
      onChange(null);
      return;
    }
    onChange({
      label: formatOnemapLocationLabel(selectedLocation),
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      radiusKm,
    });
  }, [coordinates, onChange, radiusKm, selectedLocation]);

  const clear = () => {
    setLocationInput('');
    setLocationOptions([]);
    setSelectedLocation(null);
    setRadiusInput('');
  };

  useEffect(() => {
    if (resetKey > 0) clear();
  }, [resetKey]);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
        <Autocomplete
          size="small"
          options={locationOptions}
          value={selectedLocation}
          inputValue={locationInput}
          loading={locationSearching}
          filterOptions={(options) => options}
          getOptionLabel={formatOnemapLocationLabel}
          isOptionEqualToValue={(a, b) => a.SEARCHVAL === b.SEARCHVAL && a.LATITUDE === b.LATITUDE && a.LONGITUDE === b.LONGITUDE}
          onInputChange={(_, value, reason) => {
            if (reason === 'clear') {
              clear();
              return;
            }
            if (reason !== 'input') return;
            setLocationInput(value);
            if (!value.trim()) {
              clear();
              return;
            }
            if (selectedLocation && value !== formatOnemapLocationLabel(selectedLocation)) {
              setSelectedLocation(null);
              setRadiusInput('');
            }
          }}
          onChange={(_, value) => {
            if (!value) {
              clear();
              return;
            }
            if (!parseOnemapCoordinates(value)) return;
            setSelectedLocation(value);
            setLocationInput(formatOnemapLocationLabel(value));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Reference Project/Location"
              placeholder="Enter a project or location"
              inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {locationSearching ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& input': { color: c.text, fontSize: emphasis ? '0.88rem' : '0.72rem', py: emphasis ? 0.9 : undefined },
                '& .MuiInputLabel-root': { color: c.textMuted, fontSize: emphasis ? '0.82rem' : '0.72rem' },
                '& .MuiInputLabel-root.Mui-focused': { color: c.orange },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
              }}
            />
          )}
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 92 }, minWidth: 0 }}>
        <TextField
          fullWidth
          size="small"
          label="Radius (km)"
          disabled={!selectedLocation}
          placeholder="e.g. 5"
          value={radiusInput}
          inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
          onChange={(event) => setRadiusInput(event.target.value.replace(/[^\d.]/g, ''))}
          error={radiusMissing}
          sx={{
            '& input': { color: c.text, fontSize: emphasis ? '0.88rem' : '0.72rem', py: emphasis ? 0.9 : undefined },
            '& .MuiInputLabel-root': { color: c.textMuted, fontSize: emphasis ? '0.82rem' : '0.72rem' },
            '& .MuiInputLabel-root.Mui-focused': { color: c.orange },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: radiusMissing ? c.orange : c.border,
              borderWidth: radiusMissing ? 2 : 1,
            },
          }}
        />
      </Box>
    </Box>
  );
}
